import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Trophy, Users, Calendar, Coins, X, Plus, Minus, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function TournamentDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("bracket");
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState({
    team_name: "", captain_name: "", captain_phone: "", captain_email: "",
    players: [{ name: "", phone: "" }],
  });

  const load = () => api.get(`/tournaments/${id}`).then((r) => setT(r.data));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div className="min-h-[60vh] flex items-center justify-center text-white/40">Loading…</div>;

  const teamById = Object.fromEntries(t.teams.map((x) => [x.id, x]));
  const rounds = {};
  t.matches.forEach((m) => { rounds[m.round] = rounds[m.round] || []; rounds[m.round].push(m); });
  const roundKeys = Object.keys(rounds).sort((a, b) => parseInt(a) - parseInt(b));

  const submit = async () => {
    try {
      const { data: team } = await api.post("/tournaments/register", {
        tournament_id: id,
        team_name: form.team_name,
        captain_name: form.captain_name,
        captain_phone: form.captain_phone,
        captain_email: form.captain_email,
        players: form.players.filter((p) => p.name),
        payment_method: "stripe",
      });
      if (t.entry_fee > 0) {
        const { startCheckout } = await import("@/lib/checkout");
        await startCheckout({
          kind: "tournament_team",
          ref_id: team.id,
          return_to: `/tournaments/${t.slug || t.id}`,
          onSuccess: () => {
            toast.success("Team registered & payment received!");
            setShowRegister(false);
            setForm({ team_name: "", captain_name: "", captain_phone: "", captain_email: "", players: [{ name: "", phone: "" }] });
            load();
          },
        });
        return;
      }
      toast.success("Team registered · Free entry");
      setShowRegister(false);
      setForm({ team_name: "", captain_name: "", captain_phone: "", captain_email: "", players: [{ name: "", phone: "" }] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addPlayer = () => setForm({ ...form, players: [...form.players, { name: "", phone: "" }] });
  const removePlayer = (i) => setForm({ ...form, players: form.players.filter((_, ix) => ix !== i) });
  const setPlayer = (i, k, v) => {
    const p = [...form.players]; p[i] = { ...p[i], [k]: v }; setForm({ ...form, players: p });
  };

  const paidTeams = t.teams.filter((x) => ["paid", "free"].includes(x.payment_status)).length;
  const canRegister = t.status === "registrations_open" && paidTeams < t.max_teams;

  return (
    <div data-testid="tournament-detail">
      <div className="relative">
        <img src={t.banner_url} alt={t.name} className="w-full h-72 md:h-96 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/70 to-transparent" />
        <div className="absolute inset-0 flex items-end max-w-7xl mx-auto px-5 md:px-8 pb-8">
          <div>
            <Link to="/tournaments" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 font-mono uppercase tracking-widest">
              <ArrowLeft size={14} /> All Tournaments
            </Link>
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange">{t.sport} · {t.format.replace("_", " ")}</div>
            <h1 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter mt-3">{t.name}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          <Stat icon={Calendar} label="Dates" value={`${t.start_date?.slice(5)} – ${t.end_date?.slice(5)}`} />
          <Stat icon={Users} label="Teams" value={`${paidTeams}/${t.max_teams}`} />
          <Stat icon={Coins} label="Entry Fee" value={t.entry_fee ? formatINR(t.entry_fee) : "Free"} />
          <Stat icon={Trophy} label="Prize Pool" value={formatINR(t.prize_pool)} tone="lime" />
          <div className="border border-white/12 bg-[#131A2B] p-4 flex flex-col justify-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Status</div>
            <div className="font-display text-lg font-bold brand-orange uppercase">{t.status.replace(/_/g, " ")}</div>
          </div>
        </div>

        {canRegister && (
          <button onClick={() => setShowRegister(true)} data-testid="register-team-btn" className="mb-10 bg-brand-blue text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue">
            Register Your Team → {t.entry_fee ? `Pay ${formatINR(t.entry_fee)}` : "Free"}
          </button>
        )}

        <div className="flex gap-2 mb-6">
          {["bracket", "teams", "matches", "rules"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-5 py-2 border font-mono text-xs uppercase tracking-widest ${
                tab === k ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/60"
              }`}
            >
              {k}
            </button>
          ))}
        </div>

        {tab === "bracket" && (
          <div className="overflow-x-auto" data-testid="bracket-view">
            {roundKeys.length === 0 ? (
              <div className="text-center py-16 text-white/40">Bracket will appear once registrations close and matches are generated.</div>
            ) : (
              <div className="flex gap-6 min-w-max pb-4">
                {roundKeys.map((r) => (
                  <div key={r} className="min-w-[240px]">
                    <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">
                      Round {r}{parseInt(r) === roundKeys.length ? " · Final" : ""}
                    </div>
                    <div className="space-y-3">
                      {rounds[r].map((m) => (
                        <MatchCard key={m.id} match={m} teamById={teamById} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "teams" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {t.teams.map((team) => (
              <div key={team.id} className="border border-white/12 bg-[#131A2B] p-4">
                <div className="font-display text-lg uppercase font-bold">{team.name}</div>
                <div className="text-xs text-white/50 font-mono">Captain: {team.captain_name}</div>
                <div className="text-xs text-white/40 mt-1">{team.players?.length || 0} players</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest">
                  <span className={team.payment_status === "paid" ? "brand-blue" : "text-yellow-400"}>
                    ● {team.payment_status}
                  </span>
                </div>
              </div>
            ))}
            {t.teams.length === 0 && <div className="text-white/40 py-12 text-center col-span-3">No registrations yet.</div>}
          </div>
        )}

        {tab === "matches" && (
          <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
                  <th className="text-left p-4">R</th>
                  <th className="text-left p-4">Match</th>
                  <th className="text-left p-4">When</th>
                  <th className="text-left p-4">Venue</th>
                  <th className="text-left p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {t.matches.map((m) => (
                  <tr key={m.id} className="border-b border-white/5">
                    <td className="p-4 font-mono brand-orange">{m.round}</td>
                    <td className="p-4">
                      <span className={m.winner_id === m.team_a_id ? "brand-blue" : ""}>{teamById[m.team_a_id]?.name || "TBD"}</span>
                      <span className="text-white/40 mx-2">vs</span>
                      <span className={m.winner_id === m.team_b_id ? "brand-blue" : ""}>{teamById[m.team_b_id]?.name || "TBD"}</span>
                      {m.score_a && <span className="ml-3 font-mono text-xs brand-orange">({m.score_a} — {m.score_b})</span>}
                    </td>
                    <td className="p-4 text-xs font-mono">{m.scheduled_at?.replace("T", " ").slice(0, 16) || "—"}</td>
                    <td className="p-4 text-xs">{m.venue_resource ? `${m.venue_resource}` : "—"}</td>
                    <td className="p-4"><span className="text-xs font-mono uppercase tracking-widest">{m.status}</span></td>
                  </tr>
                ))}
                {t.matches.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-white/40">No matches yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "rules" && (
          <div className="max-w-2xl">
            <h3 className="font-display text-2xl uppercase font-bold mb-4">Description</h3>
            <p className="text-white/70 whitespace-pre-wrap">{t.description || "—"}</p>
            <h3 className="font-display text-2xl uppercase font-bold mt-8 mb-4">Rules</h3>
            <p className="text-white/70 whitespace-pre-wrap">{t.rules || "—"}</p>
          </div>
        )}
      </div>

      {showRegister && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowRegister(false)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">Register Team</h3>
              <button onClick={() => setShowRegister(false)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} placeholder="Team name" required data-testid="reg-team-name" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <input value={form.captain_name} onChange={(e) => setForm({ ...form, captain_name: e.target.value })} placeholder="Captain name" required data-testid="reg-captain-name" className="bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.captain_phone} onChange={(e) => setForm({ ...form, captain_phone: e.target.value })} placeholder="Captain phone" required data-testid="reg-captain-phone" className="bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.captain_email} onChange={(e) => setForm({ ...form, captain_email: e.target.value })} placeholder="Captain email" data-testid="reg-captain-email" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-xs uppercase tracking-widest text-white/50">Roster</div>
                <button onClick={addPlayer} data-testid="add-player" className="text-xs brand-blue flex items-center gap-1"><Plus size={12} /> Add Player</button>
              </div>
              <div className="space-y-2">
                {form.players.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={p.name} onChange={(e) => setPlayer(i, "name", e.target.value)} placeholder={`Player ${i + 1} name`} className="flex-1 bg-transparent border border-white/20 py-2 px-3" />
                    <input value={p.phone} onChange={(e) => setPlayer(i, "phone", e.target.value)} placeholder="Phone" className="w-40 bg-transparent border border-white/20 py-2 px-3" />
                    {form.players.length > 1 && (
                      <button onClick={() => removePlayer(i)} className="p-2 text-white/40 hover:text-red-400"><Minus size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 border-t border-white/12 pt-6 flex items-center justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-white/50">Entry Fee (mock Razorpay)</div>
                <div className="font-display text-3xl brand-blue font-black">{t.entry_fee ? formatINR(t.entry_fee) : "FREE"}</div>
              </div>
              <button onClick={submit} data-testid="reg-submit" className="bg-brand-blue text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue">
                {t.entry_fee ? "Pay & Register" : "Register"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4">
      <div className="flex items-center gap-1 text-white/40 font-mono text-[9px] uppercase tracking-widest">
        <Icon size={10} /> {label}
      </div>
      <div className={`font-display text-lg font-bold mt-1 ${tone === "lime" ? "brand-blue" : ""}`}>{value}</div>
    </div>
  );
}

function MatchCard({ match, teamById }) {
  const a = teamById[match.team_a_id];
  const b = teamById[match.team_b_id];
  const wonA = match.winner_id && match.winner_id === match.team_a_id;
  const wonB = match.winner_id && match.winner_id === match.team_b_id;
  return (
    <div className="border border-white/12 bg-[#131A2B] hover:border-white/30 transition-colors">
      <TeamRow name={a?.name} score={match.score_a} won={wonA} bye={!a && b} />
      <div className="border-t border-white/12" />
      <TeamRow name={b?.name} score={match.score_b} won={wonB} bye={!b && a} />
      <div className="px-3 py-1.5 border-t border-white/12 bg-black/40 flex justify-between text-[9px] font-mono uppercase tracking-widest text-white/50">
        <span>#{match.match_number}</span>
        <span className={match.status === "completed" ? "brand-blue" : match.status === "scheduled" ? "brand-orange" : ""}>
          {match.status}
        </span>
      </div>
    </div>
  );
}

function TeamRow({ name, score, won, bye }) {
  return (
    <div className={`flex items-center justify-between p-3 ${won ? "bg-brand-blue/10" : ""}`}>
      <span className={`text-sm ${won ? "brand-blue font-bold" : "text-white/80"} ${bye ? "italic text-white/40" : ""}`}>
        {name || (bye ? "— bye —" : "TBD")}
      </span>
      <span className="font-mono text-sm">{score || (won ? "W" : "—")}</span>
    </div>
  );
}
